import axios from 'axios';
import { atom, useAtom, type Atom } from 'jotai';

import { getConfig } from '../../../../../config';
import { JSON_CONTENT_TYPE } from '../../../../state/graphqlEndpoints';

// Types
export interface NodeAlias {
	name: string;
	displayName: string;
	isLatest: boolean;
	deprecated: boolean;
	isAliasExternallyAvailable: boolean;
	isHiddenFromGraphQLSchema: boolean;
}

export interface Node {
	id: string;
	name: string;
	types: string[];
	displayName: string;
	persistedIn: string[];
	ownerInfo: {
		serviceId: string;
		pointOfContact: string;
		team: string;
		opsgenieTeamId: string;
	};
	graphQL: {
		hydration: {
			type: string[];
		};
	};
	metadataDefinition: string;
	mandatoryMetadataFieldsOptOut: {
		createdAt: boolean;
		lastUpdated: boolean;
		createdBy: boolean;
		updatedBy: boolean;
	};
	dataChangeStreams: {
		updateRecords: Array<{
			manifoldId: string;
			metadata: string;
			derivedNodeId: string;
			metadataFieldId: string;
			metadataFieldValue: string;
		}>;
		deleteRecords: Array<{
			manifoldId: string;
		}>;
	};
	version: string;
	category: string;
	providerType: string;
	deleted: boolean;
	isNodeExternallyAvailable: boolean;
	aliases?: NodeAlias[];
}

// Always use the direct URL - CORS should be handled by browser extension or server config
const getNodesEndpoint = () => {
	return getConfig().graphStoreUrl + '/api/node-types/registered-types.json';
};

export const nodesAsyncAtom: Atom<Promise<Record<string, Node>>> = atom(async () => {
	const endpoint = getNodesEndpoint();
	const response = await axios.get<Record<string, Node>>(endpoint, {
		headers: {
			'Content-Type': JSON_CONTENT_TYPE,
		},
		withCredentials: true,
	});
	return response.data;
});

export function useNodes(): {
	[k: string]: Node;
} {
	const [value] = useAtom(nodesAsyncAtom);

	if (!value) {
		return {};
	}

	// Filter out nodes with hidden = true
	const filteredNodes = Object.fromEntries(
		Object.entries(value).filter(([_, node]) => !node.deleted),
	);

	return filteredNodes;
}
